import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { Mail, MapPin, Phone, ArrowUpRight, Send, Linkedin, Github } from "lucide-react";
import { personal } from "../data/mock";
import { useToast } from "../hooks/use-toast";

const SERVICE_ID  = "service_rpdhhtq";
const TEMPLATE_ID = "template_nmfjj0s";
const PUBLIC_KEY  = "1Jg6CJ0HobA7Nlwht";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm]       = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.message) {
      toast({ title: "Missing fields", description: "Name, email and message are required." });
      return;
    }

    setSending(true);

    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name:  form.name,
          from_email: form.email,
          subject:    form.subject || "No subject",
          message:    form.message,
          to_email:   "harshtripathiyt@gmail.com",
        },
        PUBLIC_KEY
      );

      setForm({ name: "", email: "", subject: "", message: "" });
      toast({ title: "Message sent ✓", description: "Thanks — I'll get back within a day." });
    } catch (err) {
      console.error("EmailJS error:", err);
      toast({
        title: "Failed to send",
        description: "Something went wrong. Please try again or email me directly.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <section className="border-b hairline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-12">
          <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-5">
            07 — Contact
          </div>
          <h1 className="display-xl text-5xl md:text-8xl leading-[0.95]">
            Let's<br />
            <span className="italic font-light">talk.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-muted-foreground text-lg leading-relaxed">
            Roles, consulting, collaborations or just a thoughtful question — my inbox is open.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-12 gap-10">

        {/* Form */}
        <form onSubmit={onSubmit} className="col-span-12 md:col-span-7 space-y-8">
          <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
            — 07.1 Write to me
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Your name"  value={form.name}  onChange={onChange("name")}  placeholder="Jane Doe" />
            <Field label="Email" type="email" value={form.email} onChange={onChange("email")} placeholder="jane@company.com" />
          </div>

          <Field label="Subject" value={form.subject} onChange={onChange("subject")} placeholder="A collaboration, a role, a question…" />

          <div>
            <label className="block font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-3">
              Message
            </label>
            <textarea
              value={form.message}
              onChange={onChange("message")}
              rows={7}
              placeholder="Tell me a bit about what you're building…"
              className="w-full bg-transparent border-b hairline py-3 px-1 outline-none focus:border-foreground transition-colors resize-none"
            />
          </div>

          <div className="flex items-center gap-4">
            <button type="submit" disabled={sending} className="btn-primary disabled:opacity-60" data-cursor-hover>
              {sending ? "Sending…" : (<>Send message <Send size={15} /></>)}
            </button>
            <span className="font-mono text-[11px] text-muted-foreground">Replies within 24h.</span>
          </div>
        </form>

        {/* Aside */}
        <aside className="col-span-12 md:col-span-5 space-y-10">
          <div>
            <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-5">
              — 07.2 Direct
            </div>
            <div className="divide-y hairline border-t border-b hairline">
              <DirectRow icon={<Mail size={14} />}    label="Email"    value={personal.email}    href={`mailto:${personal.email}`} />
              <DirectRow icon={<Phone size={14} />}   label="Phone"    value={personal.phone}    href={`tel:${personal.phone.replace(/\s/g, "")}`} />
              <DirectRow icon={<MapPin size={14} />}  label="Location" value={personal.location} />
            </div>
          </div>

          <div>
            <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-5">
              — 07.3 Social
            </div>
            <div className="grid grid-cols-2 gap-3">
              <SocialCard href={personal.linkedin} icon={<Linkedin size={18} />} label="LinkedIn" handle="in/harshtripathi-in" />
              <SocialCard href={personal.github}   icon={<Github   size={18} />} label="GitHub"   handle="@harshtripathi-in" />
            </div>
          </div>

          <div className="p-6 border hairline rounded-md">
            <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-2">
              Currently
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-foreground animate-pulse" />
              {personal.status}
            </div>
          </div>
        </aside>

      </section>
    </div>
  );
};

const Field = ({ label, type = "text", value, onChange, placeholder }) => (
  <div>
    <label className="block font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-3">
      {label}
    </label>
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full bg-transparent border-b hairline py-3 px-1 outline-none focus:border-foreground transition-colors"
    />
  </div>
);

const DirectRow = ({ icon, label, value, href }) => {
  const Inner = () => (
    <div className="py-4 flex items-start justify-between gap-6 group">
      <div>
        <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest flex items-center gap-2">
          {icon} {label}
        </div>
        <div className="mt-2 text-sm">{value}</div>
      </div>
      {href && <ArrowUpRight size={16} className="text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition" />}
    </div>
  );
  return href ? <a href={href} className="block" data-cursor-hover><Inner /></a> : <div><Inner /></div>;
};

const SocialCard = ({ href, icon, label, handle }) => (
  <a href={href} target="_blank" rel="noreferrer"
    className="p-5 border hairline rounded-md hover:bg-muted/40 transition-colors group" data-cursor-hover>
    <div className="flex items-center justify-between">
      <div className="w-9 h-9 rounded-md border hairline grid place-items-center">{icon}</div>
      <ArrowUpRight size={16} className="text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition" />
    </div>
    <div className="mt-4 font-medium">{label}</div>
    <div className="text-xs text-muted-foreground mt-0.5">{handle}</div>
  </a>
);

export default Contact;